import { useAppDataStore } from "@/mobx/appDataStore.jsx";
import { DataTable } from "components/dataTable/index.jsx";
import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import moment from "moment";
import formatters from "helpers/formatters.jsx";

export default observer(function ConsignmentReports(props) {
  const appDataStore = useAppDataStore();
  const [statusAction, setStatusAction] = useState({ reportId: null, action: null });
  const [rejectionReason, setRejectionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "approved":
        return "Aprobado";
      case "rejected":
        return "Rechazado";
      case "pending":
        return "Pendiente";
      default:
        return status;
    }
  };

  const handleStatusChange = async (reportId, newStatus, reason = "") => {
    try {
      setActionLoading(true);
      await appDataStore.post("/consignment-report/update-status", {
        reportId,
        status: newStatus,
        rejectionReason: reason
      });

      alert("Reporte actualizado exitosamente");
      setStatusAction({ reportId: null, action: null });
      setRejectionReason("");
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("Error al actualizar reporte:", err);
      alert("Error al actualizar reporte: " + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const columns = useMemo(() => ([
    {
      accessorKey: 'user',
      header: 'Usuario',
      enableColumnFilter: false,
      cell: ({cell}) => {
        const user = cell.getValue();
        return user ? `${user.name} (${user.email})` : "Usuario desconocido";
      }
    },
    {
      accessorKey: 'reference',
      header: 'Referencia',
    },
    {
      accessorKey: 'amount',
      header: 'Monto',
      cell: ({cell}) => formatters.cash(cell.getValue()),
      exportFormat: (row) => row.amount
    },
    {
      accessorKey: 'balanceType',
      header: 'Tipo de Saldo',
    },
    {
      accessorKey: 'photo',
      header: 'Foto',
      enableColumnFilter: false,
      cell: ({cell}) => {
        const photo = cell.getValue();
        return photo ? (
          <a href={photo} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
            Ver foto
          </a>
        ) : (
          <span className="text-gray-500">Sin foto</span>
        );
      }
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      opFilter: 'eq',
      cell: ({cell}) => {
        const status = cell.getValue();
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(status)}`}>
            {getStatusLabel(status)}
          </span>
        );
      }
    },
    {
      accessorKey: 'createdAt',
      header: 'Fecha',
      cell: ({cell}) => moment(cell.getValue()).format('DD/MM/YYYY HH:mm'),
      exportFormat: (row) => moment(row.createdAt).format('DD/MM/YYYY HH:mm')
    },
    {
      id: 'actions',
      header: 'Acciones',
      enableColumnFilter: false,
      cell: ({row}) => {
        const report = row.original;
        return report.status === "pending" ? (
          <div className="flex gap-2">
            <button
              onClick={() => handleStatusChange(report.id, "approved")}
              disabled={actionLoading}
              className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:bg-gray-400"
            >
              Aprobar
            </button>
            <button
              onClick={() => setStatusAction({ reportId: report.id, action: "reject" })}
              className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
            >
              Rechazar
            </button>
          </div>
        ) : (
          <span className="text-gray-500 text-xs">
            {report.status === "rejected" && report.rejectionReason
              ? `Rechazado: ${report.rejectionReason}`
              : "Sin acciones"}
          </span>
        );
      }
    }
  ]), [actionLoading]);

  const dataFetch = async (records, currentPage, order, search, filter) => {
    return appDataStore.post(`/app/datasource/ConsignmentReport`, {
      records,
      currentPage,
      order,
      search,
      filter,
      attributes: ['id', 'photo', 'amount', 'reference', 'balanceType', 'status', 'rejectionReason', 'createdAt'],
      include: [
        {
          model: 'User',
          as: 'user',
          attributes: ['id', 'name', 'email']
        }
      ],
      searchAttributes: ['reference']
    });
  };

  return (
    <>
      <div className="text-black">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Reportes de Consignación</h3>

        <DataTable
          exportFileName={`reportes_consignacion_${moment().format('DDMMYY_HHmm')}`}
          columns={columns}
          dataFetch={dataFetch}
          defaultSorting={[{id: 'createdAt', desc: true}]}
          refreshDependencies={refreshKey}
        />

        {/* Modal para rechazar */}
        {statusAction.action === "reject" && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-96">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Rechazar Reporte</h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Razón del rechazo
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Ingresa la razón del rechazo..."
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  rows="4"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleStatusChange(statusAction.reportId, "rejected", rejectionReason)}
                  disabled={actionLoading || !rejectionReason}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400"
                >
                  {actionLoading ? "Procesando..." : "Confirmar Rechazo"}
                </button>
                <button
                  onClick={() => {
                    setStatusAction({ reportId: null, action: null });
                    setRejectionReason("");
                  }}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
});
